import { Box, Typography, useTheme } from '@mui/material';
import { CorporateFareOutlined } from '@mui/icons-material';
import SettingCard from './SettingCard';
import WolfButton from '@components/ui/WolfButton';
import WolfTooltip from '@components/ui/WolfTooltip';
import { useTranslation } from '@hooks/useTranslation';
import { UserRoles } from '@models/user.model';
import { useHistory } from 'react-router-dom';

interface Organization {
  organizationId: string;
  name: string;
  description?: string;
  role?: UserRoles;
}

interface OrganizationsSectionProps {
  organizations: Organization[];
  currentOrgId: string;
  currentRole: UserRoles | string | null;
  onSwitch: (orgId: string, name: string, description?: string) => Promise<void>;
  onLeave: (orgId: string) => void;
  canLeaveOrganization: (orgId: string) => boolean;
  getLeaveTooltipText: (orgId: string) => string;
}

const OrganizationsSection: React.FC<OrganizationsSectionProps> = ({
  organizations,
  currentOrgId,
  currentRole,
  onSwitch,
  onLeave,
  canLeaveOrganization,
  getLeaveTooltipText,
}) => {
  const theme = useTheme();
  const history = useHistory();
  const localeSettings = useTranslation('settings');

  return (
    <SettingCard
      title={localeSettings['organizationsManagement'] || localeSettings['yourOrganizations'] || 'Your Organizations'}
      icon={<CorporateFareOutlined />}
      iconColor={theme.palette.backgroundBlue.main}>
      {organizations && organizations.length > 0 ? (
        <Box className="space-y-2">
          {organizations.map((org) => (
            <Box
              key={org.organizationId}
              className={`flex flex-col sm:flex-row items-start sm:items-center justify-between p-3 border rounded-lg transition-all ${
                org.organizationId === currentOrgId
                  ? 'border-primary bg-primary/5'
                  : 'border-gray-200 hover:border-gray-300'
              }`}>
              <Box className="flex-1 mb-2 sm:mb-0">
                <Box className="flex items-center gap-2 mb-1">
                  <Typography variant="h4semibold">{org.name}</Typography>
                  <Typography variant="body2" color="textSecondary" className="!font-medium">
                    ({org.role || 'Role unavailable'})
                  </Typography>
                </Box>
                {org.description && (
                  <Typography variant="body2" color="textSecondary">
                    {org.description}
                  </Typography>
                )}
              </Box>
              <Box className="flex items-center gap-2 w-full sm:w-auto justify-end">
                {org.organizationId === currentOrgId ? (
                  <>
                    <Box className="px-2 py-1 bg-primary text-white text-xs rounded">
                      {localeSettings['currentOrganization'] || 'Current'}
                    </Box>
                    {currentRole === UserRoles.OWNER && (
                      <WolfButton color="primary" onClick={() => history.push(`/organization/${currentOrgId}`)}>
                        {localeSettings['editOrganization'] || 'Edit Org'}
                      </WolfButton>
                    )}
                  </>
                ) : (
                  <WolfButton color="primary" onClick={() => onSwitch(org.organizationId, org.name, org.description)}>
                    {localeSettings['switchToOrganization'] || 'Switch'}
                  </WolfButton>
                )}

                {/* Leave Organization Button */}
                <WolfTooltip title={getLeaveTooltipText(org.organizationId)}>
                  <span>
                    <WolfButton
                      color="secondary"
                      onClick={() => onLeave(org.organizationId)}
                      disabled={!canLeaveOrganization(org.organizationId)}
                      className="!text-red-600 !border-red-600 hover:!bg-red-50">
                      {localeSettings['leaveOrganization'] || 'Leave'}
                    </WolfButton>
                  </span>
                </WolfTooltip>
              </Box>
            </Box>
          ))}
        </Box>
      ) : (
        <Typography variant="body2" color="textSecondary">
          {localeSettings['noOrganizations'] || 'No organizations found'}
        </Typography>
      )}
    </SettingCard>
  );
};

export default OrganizationsSection;
